import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Spinner,
  Text,
  HStack,
  VStack,
  Button,
  SimpleGrid,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  List,
  ListItem,
  ListIcon,
  Divider,
} from '@chakra-ui/react';
import {
  FiRefreshCw, FiDatabase, FiSend, FiSettings, FiMail,
  FiCheckCircle, FiXCircle, FiAlertTriangle,
} from 'react-icons/fi';
import { tenantHeaders } from '../tenant';

const API_BASE = '/api/v1';

const statusColor = (status) => {
  switch (status) {
    case 'healthy': case 'complete': return 'green';
    case 'degraded': case 'incomplete': return 'yellow';
    case 'unhealthy': return 'red';
    case 'not_configured': return 'gray';
    default: return 'gray';
  }
};

const statusIcon = (status) => {
  switch (status) {
    case 'healthy': case 'complete': return FiCheckCircle;
    case 'degraded': case 'incomplete': return FiAlertTriangle;
    case 'unhealthy': return FiXCircle;
    default: return FiAlertTriangle;
  }
};

function HealthCheck() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    ...tenantHeaders(),
  });

  const fetchHealth = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE}/health/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => { setHealth(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4} color="gray.500">Running health checks…</Text>
      </Box>
    );
  }

  if (!health) {
    return <Text color="gray.500">Failed to load health check data.</Text>;
  }

  const checks = health.checks || {};

  return (
    <Box>
      <HStack justify="space-between" mb={8}>
        <Heading size="lg">Health Check</Heading>
        <Button leftIcon={<FiRefreshCw />} onClick={fetchHealth} colorScheme="orange" variant="outline" size="sm">
          Re-check
        </Button>
      </HStack>

      {/* Overall Status */}
      <Alert
        status={health.overall === 'healthy' ? 'success' : health.overall === 'degraded' ? 'warning' : health.overall === 'not_configured' ? 'info' : 'error'}
        borderRadius="xl"
        mb={8}
        py={4}
      >
        <AlertIcon boxSize={6} />
        <Box>
          <AlertTitle fontSize="lg">
            Overall: <Badge colorScheme={statusColor(health.overall)} fontSize="md" px={3} py={1} borderRadius="full">{health.overall}</Badge>
          </AlertTitle>
          <AlertDescription color="gray.600">
            Instance: {health.tenant_id
              ? (health.tenant_name ? `${health.tenant_name} (${health.tenant_id})` : health.tenant_id)
              : 'No tenant (legacy)'} • Checked at {health.timestamp || 'N/A'}
          </AlertDescription>
        </Box>
      </Alert>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
        {/* Database */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <HStack mb={4}>
            <Icon as={FiDatabase} boxSize={5} color="orange.500" />
            <Text fontWeight="bold" fontSize="lg">Database</Text>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.database?.status)} fontSize="sm" px={2} borderRadius="full">
                {checks.database?.status}
              </Badge>
            </HStack>
            {checks.database?.latency_ms !== undefined && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="semibold">{checks.database.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.database?.error && (
              <Text color="red.500" fontSize="sm">{checks.database.error}</Text>
            )}
          </VStack>
        </Box>

        {/* Brevo API */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <HStack mb={4}>
            <Icon as={FiSend} boxSize={5} color="orange.500" />
            <Text fontWeight="bold" fontSize="lg">Brevo API key</Text>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.brevo_api?.status)} fontSize="sm" px={2} borderRadius="full">
                {checks.brevo_api?.status}
              </Badge>
            </HStack>
            {checks.brevo_api?.latency_ms !== undefined && (
              <HStack justify="space-between">
                <Text color="gray.600">Latency</Text>
                <Text fontWeight="semibold">{checks.brevo_api.latency_ms} ms</Text>
              </HStack>
            )}
            {checks.brevo_api?.company_name && (
              <HStack justify="space-between">
                <Text color="gray.600">Company</Text>
                <Text fontWeight="semibold">{checks.brevo_api.company_name}</Text>
              </HStack>
            )}
            {checks.brevo_api?.plan && (
              <HStack justify="space-between">
                <Text color="gray.600">Plan</Text>
                <Text fontWeight="semibold">{checks.brevo_api.plan}</Text>
              </HStack>
            )}
            {checks.brevo_api?.error && (
              <Text color="red.500" fontSize="sm">{checks.brevo_api.error}</Text>
            )}
            {checks.brevo_api?.message && (
              <Text color={checks.brevo_api.status === 'not_configured' ? 'gray.500' : 'red.500'} fontSize="sm">
                {checks.brevo_api.message}
              </Text>
            )}
          </VStack>
        </Box>

        {/* SMTP login: every email goes out through Brevo's SMTP relay with these */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <HStack mb={4}>
            <Icon as={FiMail} boxSize={5} color="orange.500" />
            <Text fontWeight="bold" fontSize="lg">SMTP login</Text>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.smtp?.status)} fontSize="sm" px={2} borderRadius="full">
                {checks.smtp?.status}
              </Badge>
            </HStack>
            {checks.smtp?.message && (
              <Text color={checks.smtp.status === 'healthy' ? 'gray.600' : checks.smtp.status === 'not_configured' ? 'gray.500' : 'red.500'} fontSize="sm">
                {checks.smtp.message}
              </Text>
            )}
          </VStack>
        </Box>

        {/* Configuration */}
        <Box bg="white" p={6} borderRadius="xl" boxShadow="sm">
          <HStack mb={4}>
            <Icon as={FiSettings} boxSize={5} color="orange.500" />
            <Text fontWeight="bold" fontSize="lg">Configuration</Text>
          </HStack>
          <Divider mb={4} />
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between">
              <Text color="gray.600">Status</Text>
              <Badge colorScheme={statusColor(checks.configuration?.status)} fontSize="sm" px={2} borderRadius="full">
                {checks.configuration?.status}
              </Badge>
            </HStack>
            <HStack justify="space-between">
              <Text color="gray.600">Total Settings</Text>
              <Text fontWeight="semibold">{checks.configuration?.total_settings || 0}</Text>
            </HStack>
            {(checks.configuration?.missing_required || []).length > 0 && (
              <Box>
                <Text fontSize="sm" color="red.500" fontWeight="semibold" mb={1}>Missing Required:</Text>
                <List spacing={1}>
                  {checks.configuration.missing_required.map((k, i) => (
                    <ListItem key={i} fontSize="sm" color="red.500">
                      <ListIcon as={FiXCircle} color="red.400" />
                      {k}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {(checks.configuration?.missing_optional || []).length > 0 && (
              <Box>
                <Text fontSize="sm" color="yellow.600" fontWeight="semibold" mb={1}>Missing Optional:</Text>
                <List spacing={1}>
                  {checks.configuration.missing_optional.map((k, i) => (
                    <ListItem key={i} fontSize="sm" color="yellow.600">
                      <ListIcon as={FiAlertTriangle} color="yellow.500" />
                      {k}
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </VStack>
        </Box>
      </SimpleGrid>
    </Box>
  );
}

export default HealthCheck;
